import { FormIdUtil } from '@onshape/www-inertia';
import ExternalUtil from '../ExternalUtil';
import RichMap from './RichMap';
import type { UnifiedSignupScreenName } from '../facets/Form/coordinator/UnifiedSignup/UnifiedSignupScreenName';

/** errors if `T` isn't a subset of `UnifiedSignupScreenName`, so typos are caught at compile time */
type AssertSubset<T extends UnifiedSignupScreenName> = T;

/** the only screens that report a VWO screen-view goal */
type VwoScreenGoalName = AssertSubset<
  | 'uss-details-free-2of2'
  | 'uss-details-pro-2of2'
  | 'uss-details-standard'
  | 'uss-details-edu-student'
  | 'uss-details-edu-educator'
  | 'uss-details-edu-student-k12'
>;

export type VwoEventName =
  | 'sawDetailsEduStudentK12'
  | 'sawDetailsEduEducator'
  | 'sawDetailsEduStudent'
  | 'sawStandardDetails'
  | 'sawFree2Of2'
  | 'sawPro2Of2';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class VwoUtil {
  private static readonly liveVwoGoalToFormIdMap = new RichMap<string, number>({
    default: 206, // a general conversion on live
    [FormIdUtil.names.unifiedSignup]: 222, // a conversion to form ID 182 on live
  });

  private static readonly stageVwoGoalToFormIdMap = new RichMap<string, number>({
    default: 207, // a general conversion on stage
    [FormIdUtil.names.unifiedSignup]: 221, // a conversion to form ID 182 on stage
  });

  private static readonly devVwoGoalToFormIdMap = new RichMap<string, number>({
    default: 208, // a general conversion on dev
    [FormIdUtil.names.unifiedSignup]: 220, // a conversion to form ID 182 on dev
  });

  private static readonly screenToVwoEventMap = new RichMap<VwoScreenGoalName, VwoEventName>({
    'uss-details-free-2of2': 'sawFree2Of2',
    'uss-details-pro-2of2': 'sawPro2Of2',
    'uss-details-standard': 'sawStandardDetails',
    'uss-details-edu-student': 'sawDetailsEduStudent',
    'uss-details-edu-educator': 'sawDetailsEduEducator',
    'uss-details-edu-student-k12': 'sawDetailsEduStudentK12',
  });

  /** send VWO custom event using VWO event API */
  private static vwoPushEvent({ eventName }: { eventName: VwoEventName }): void {
    window.VWO = window.VWO || [];
    window.VWO.event =
      window.VWO.event ||
      ((...args: unknown[]) => {
        window.VWO.push(['event', ...args]);
      });

    console.log('pushing VWO event: ', eventName);
    window.VWO.event(eventName);
  }

  /** send VWO custom conversion for split tests */
  private static vwoPushConversion({ conversionId }: { conversionId: number }) {
    window._vis_opt_queue = window._vis_opt_queue || [];

    console.log('pushing VWO conversion: ', conversionId);

    window._vis_opt_queue.push(() => {
      if (!window._vis_opt_goal_conversion) return;
      window._vis_opt_goal_conversion(conversionId);
    });
  }

  /** track a form submission conversion for VWO */
  public static vwoSubmitFormConversionPush({ formId }: { formId?: string }) {
    window._vis_opt_queue = window._vis_opt_queue || [];

    let options = this.devVwoGoalToFormIdMap;
    if (ExternalUtil.isStage()) options = this.stageVwoGoalToFormIdMap;
    if (ExternalUtil.isProduction()) options = this.liveVwoGoalToFormIdMap;

    let conversionId = 0;
    if (!formId) {
      conversionId = options.getOrDefaultKey('default', 'default');
    } else {
      conversionId = options.getOrDefaultKey(formId, 'default');
    }

    this.vwoPushConversion({ conversionId });
  }

  /** track a screen view conversion for VWO */
  public static vwoScreenGoalPush({ screenName }: { screenName: UnifiedSignupScreenName }) {
    const eventName = this.screenToVwoEventMap.getOrNull(screenName);
    if (eventName !== null) {
      this.vwoPushEvent({ eventName });
    }
  }
}
